import { EyeOutlined } from '@ant-design/icons';
import { Button, Space, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps, FilterValueProps, mappingFilterFields,
  mappingFilterToQuery, mappingParamsFilter, mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import { getExternalResultsService } from 'common/services/survey';
import { ExternalResultDataTypes } from 'common/services/survey/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { formatDateTime } from 'common/utils/functions';
import { translationSick } from 'extends/Charts/helper';

const ExternalResultManagement: React.FC<ActiveRoles> = () => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();

  /* Selectors */
  const {
    defaultPageSize,
    advancedFilter,
  } = useAppSelector((state) => state.system);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries());

  /* States */
  const [currentPage, setCurrentPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));
  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  /* Queries */
  const { data: resultRes, isLoading } = useQuery(
    ['getAllExternalResult', currentPage, currentView, keyword, returnParamsFilter],
    () => getExternalResultsService({
      page: currentPage,
      limit: currentView,
      kioskCode: keyword,
      ...returnParamsFilter
    }),
  );

  /* Variables */
  const columns: ColumnsType<ExternalResultDataTypes> = [
    {
      title: 'ID',
      key: 'id',
      width: 55,
      align: 'center',
      fixed: 'left',
      sorter: {
        compare: (
          a: ExternalResultDataTypes,
          b: ExternalResultDataTypes
        ) => a.resultData.id - b.resultData.id,
      },
      sortDirections: ['descend', 'ascend'],
      render: (_name: string, data: ExternalResultDataTypes) => (
        <Typography.Text>
          {data.resultData?.id}
        </Typography.Text>
      ),
    },
    {
      title: t('surveyResult.source'),
      dataIndex: 'kioskCode',
      key: 'kioskCode',
      render: (_: string, data: ExternalResultDataTypes) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {data.resultData?.kioskCode}
        </Typography.Text>
      ),
    },
    {
      title: t('surveyResult.heartRisk'),
      dataIndex: 'heartRiskCode',
      key: 'heartRiskCode',
      render: (_: string, data: ExternalResultDataTypes) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {t(translationSick(data.resultData?.heartRiskCode))}
        </Typography.Text>
      ),
    },
    {
      title: t('surveyResult.kidneyRiskCode'),
      dataIndex: 'kidneyRiskCode',
      key: 'kidneyRiskCode',
      render: (_: string, data: ExternalResultDataTypes) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {t(translationSick(data.resultData?.kidneyRiskCode))}
        </Typography.Text>
      ),
    },
    {
      title: t('surveyResult.heartRiskScore'),
      dataIndex: 'heartRiskScore',
      key: 'heartRiskScore',
      render: (_: string, data: ExternalResultDataTypes) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {data.resultData?.heartRiskScore}
        </Typography.Text>
      ),
    },
    {
      title: t('surveyResult.locCauThan'),
      dataIndex: 'locCauThan',
      key: 'locCauThan',
      render: (_: string, data: ExternalResultDataTypes) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {data.resultData?.locCauThan}
        </Typography.Text>
      ),
    },
    {
      title: t('surveyResult.proteinuria'),
      dataIndex: 'proteinuria',
      key: 'proteinuria',
      render: (_: string, data: ExternalResultDataTypes) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {t(translationSick(data.resultData?.proteinuria))}
        </Typography.Text>
      ),
    },
    {
      title: t('surveyResult.cdk'),
      dataIndex: 'cdk',
      key: 'cdk',
      render: (_: string, data: ExternalResultDataTypes) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {t(translationSick(data.resultData?.cdk))}
        </Typography.Text>
      ),
    },
    // Ngày submitted
    {
      title: t('surveyResult.submittedAt'),
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      sorter: {
        compare: (a: ExternalResultDataTypes, b: ExternalResultDataTypes) => {
          const aDate = new Date(a.resultData?.submittedAt || '');
          const bDate = new Date(b.resultData?.submittedAt || '');
          return Number(aDate) - Number(bDate);
        },
      },
      sortDirections: ['descend', 'ascend'],
      render: (_: string, data: ExternalResultDataTypes) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {formatDateTime(data.resultData?.submittedAt)}
        </Typography.Text>
      ),
    },
    // --- Ngày tạo
    {
      title: t('system.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: {
        compare: (a: ExternalResultDataTypes, b: ExternalResultDataTypes) => {
          const aDate = new Date(a.resultData?.createdAt || '');
          const bDate = new Date(b.resultData?.createdAt || '');
          return Number(aDate) - Number(bDate);
        },
      },
      sortDirections: ['descend', 'ascend'],
      render: (_: string, data: ExternalResultDataTypes) => (
        <Typography.Text
          style={{ color: '#4a4a4a' }}
        >
          {formatDateTime(data.resultData?.createdAt)}
        </Typography.Text>
      ),
    },
    // --- Thao tác
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      render: (_name: string, _data: ExternalResultDataTypes) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`${ROUTE_PATHS.HEALTH_SERVICE_RESULT_DETAIL}?id=${_data.resultData?.id}`)}
          />
        </Space>
      ),
    },
  ];

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, {
      ...data,
      index: counter
    }]);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
  };

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('surveyResult', advancedFilter),
    [advancedFilter]
  );

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  useEffect(() => {
    setSearchParams(mappingFilterToQuery(selectedFilterList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterList]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.healthServiceResult')}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          isLoading={isLoading}
          handleSearch={setKeyword}
          tableProps={{
            initShowColumns: ['id', 'kioskCode', 'heartRiskCode', 'kidneyRiskCode', 'heartRiskScore', 'locCauThan', 'proteinuria', 'cdk', 'submittedAt', 'action'],
            columns,
            pageData: resultRes?.data || [],
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: resultRes?.meta.total || 1,
            noBaseCol: true,
            filterFields,
          }}
          noCheckbox
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
        />
      </div>
    </>
  );
};

export default ExternalResultManagement;
